import { VaultSDK, DelegationAuthProvider } from '@cef-ai/vault-sdk';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';

/**
 * The DATA-PLANE READ instance: a `VaultSDK` authenticated with a pre-minted
 * Bearer delegation token instead of a live signature.
 *
 * Why this exists, and why it is not just a style preference. `resume()`
 * restores the bearer session but NOT the PRF-derived signing keys — that is a
 * documented invariant of @cef-ai/wallet (keys live only in memory; the sole
 * durable artifact is the HttpOnly refresh cookie), so `hasKeys` is false after
 * EVERY reload. A direct-sign read on boot would therefore raise
 * `needs-step-up`, and the step-up popup must be opened synchronously inside a
 * user gesture — which a boot effect or a poll timer does not have, so the
 * browser blocks it. `requestDelegation` needs only `isAuthenticated`, so it
 * succeeds bearer-active with no keys, no ceremony and no popup.
 *
 * Sibling of `./browser-client.ts` (direct-sign) and `./signing-sdk.ts`
 * (wallet-backed signing). The three must never be collapsed: `VaultSDKConfig`
 * is `signer` XOR `auth`, and `tests/unit/clients/vault/auth-split.test.ts`
 * pins which provider each factory really resolves.
 */

/**
 * Capabilities the read instance needs. vault-api's allowlist is hardcoded
 * server-side to `vault-agreement` / `ddc-read` / `ddc-write` / `gar-record`.
 * This instance's actual usage is `vault.current`, `scopes.list`,
 * `agents.list`, and the `jobs.list` poll — reads only, so `ddc-read` and
 * `vault-agreement` cover it. `ddc-write` is NOT needed here: agent-connect
 * and every other write (scope create/edit/delete, disconnect) sign through
 * the signing SDK's `session.signingHandle()`, never through this Bearer
 * instance. It is kept for now anyway — dropping it needs vault-api's
 * server-side allowlist confirmed safe without it first, and would require
 * amending the `auth-split.test.ts` pin. Tracked as issue #46.
 */
export const VAULT_READ_CAPABILITIES = ['ddc-read', 'ddc-write', 'vault-agreement'] as const;

/** vault-api caps delegation TTL at 24h; 1h is well inside it. */
export const VAULT_READ_TTL = '1h';

/**
 * Fraction of the token's REMAINING lifetime after which we re-mint. Driven
 * from the `expiresAt` the issuer actually returned rather than re-derived
 * from `VAULT_READ_TTL`, so a server-side TTL change cannot desynchronise the
 * schedule from the real expiry.
 */
const REFRESH_AT_FRACTION = 0.8;

/**
 * Delay used when `expiresAt` is absent, unparseable, or already in the past.
 * 80% of the nominal `VAULT_READ_TTL` ('1h'). Without it those cases would
 * schedule at `NaN` (a timer that never fires, i.e. the bug this fixes) or at
 * <= 0 (a re-mint loop hammering the issuer).
 */
export const VAULT_READ_FALLBACK_REFRESH_MS = 48 * 60 * 1000;

/**
 * Floor on any computed delay. An issuer that hands back a token expiring in
 * seconds would otherwise put the timer into a hot re-mint loop.
 */
const MIN_REFRESH_MS = 30 * 1000;

/**
 * Ceiling on any computed delay — 80% of vault-api's own 24h TTL cap.
 *
 * This is not cosmetic. `setTimeout` coerces any delay above 2^31-1 ms (~24.8
 * days) to 1 ms, and since every re-mint re-derives the SAME far-future delay
 * from the same bad `expiresAt`, the result is not one early fire but an
 * unbounded loop hammering `requestDelegation` — the exact failure the floor
 * exists to prevent, arriving from the opposite end where the floor cannot see
 * it. Reachable two ways: a client clock ~25+ days slow, or an issuer that
 * ignores the 24h cap.
 */
const MAX_REFRESH_MS = 24 * 60 * 60 * 1000 * 0.8;

/**
 * Delay before retrying after a FAILED re-mint. Short relative to the TTL, so
 * a transient gateway blip is recovered from well inside the headroom the
 * still-valid current token has left.
 */
export const VAULT_READ_REFRESH_RETRY_MS = 60 * 1000;

/**
 * How long to wait before re-minting a token that expires at `expiresAt`.
 * Exported for direct unit testing of the boundary cases — the scheduler
 * itself only ever calls it with the issuer's own value.
 */
export function readTokenRefreshDelayMs(expiresAt: string | undefined, nowMs: number): number {
  const remainingMs = (expiresAt ? Date.parse(expiresAt) : NaN) - nowMs;
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return VAULT_READ_FALLBACK_REFRESH_MS;
  const delayMs = remainingMs * REFRESH_AT_FRACTION;
  return Math.min(Math.max(delayMs, MIN_REFRESH_MS), MAX_REFRESH_MS);
}

interface CreateVaultReadClientArgs {
  vaultBaseUrl: string;
  token: string;
  garUrl?: string;
  marketplaceEndpoint?: string;
}

/** Build a Bearer-authenticated `VaultSDK` from an already-minted token. */
export function createVaultReadClient(a: CreateVaultReadClientArgs): VaultSDK {
  return new VaultSDK({
    endpoint: a.vaultBaseUrl,
    auth: new DelegationAuthProvider(a.token),
    ...(a.garUrl ? { garEndpoint: a.garUrl } : {}),
    ...(a.marketplaceEndpoint ? { marketplaceEndpoint: a.marketplaceEndpoint } : {}),
  });
}

/**
 * Mint a delegation and build the read instance in one step — the ONE-SHOT
 * form. The token it embeds is never renewed, so this is only correct for a
 * client that is superseded well inside `VAULT_READ_TTL` (today: the
 * short-lived `VaultConnectFlow` claim). A client that has to survive a
 * session must use `mintRefreshingVaultReadClient` below.
 *
 * Deliberately takes `vaultBaseUrl` from the caller (i.e. from `useConfig()`)
 * rather than resolving an env name against a baked-in endpoint table. The
 * earlier iteration of this factory hardcoded a `Record<PlatformEnv, string>`,
 * which contradicts AGENTS.md ("runtime config is loaded once at app bootstrap
 * and exposed through `useConfig()`") and would have silently ignored the
 * `VITE_VAULT_BASE_URL` that `/config.js` sets per deployment.
 */
export async function mintVaultReadClient(
  wallet: Pick<CereWallet, 'requestDelegation'>,
  a: Omit<CreateVaultReadClientArgs, 'token'>,
): Promise<VaultSDK> {
  return (await mintDelegation(wallet, a)).sdk;
}

/** As `mintVaultReadClient`, but keeps the issuer's expiry for the scheduler. */
async function mintDelegation(
  wallet: Pick<CereWallet, 'requestDelegation'>,
  a: Omit<CreateVaultReadClientArgs, 'token'>,
): Promise<{ sdk: VaultSDK; expiresAt: string | undefined }> {
  const delegation = await wallet.requestDelegation({
    capabilities: [...VAULT_READ_CAPABILITIES],
    appId: 'vault',
    ttl: VAULT_READ_TTL,
  });
  return {
    sdk: createVaultReadClient({ ...a, token: delegation.token }),
    expiresAt: delegation.expiresAt,
  };
}

export interface RefreshingVaultReadClient {
  /** The instance minted right now. Later ones arrive via `onRefresh`. */
  sdk: VaultSDK;
  /** Cancel the pending re-mint. Idempotent, and safe to call mid-flight. */
  dispose: () => void;
}

export interface RefreshingVaultReadClientHandlers {
  /**
   * A silent re-mint succeeded — swap this in as the read instance.
   * `DelegationAuthProvider` holds its token as `private readonly` with no
   * refresh hook, so refreshing genuinely means replacing the whole `VaultSDK`;
   * anything already bound to the previous one (a `Vault` handle owns the
   * `VaultHttpClient` it was built from) has to be re-bound by the caller.
   */
  onRefresh: (sdk: VaultSDK) => void;
  /** A re-mint failed. The previous instance is kept and a retry is scheduled. */
  onError: (error: unknown) => void;
}

/**
 * Mint the read instance and keep re-minting it before the token expires.
 *
 * Without this the Bearer token is minted exactly once, so after
 * `VAULT_READ_TTL` every data-plane read 401s in a tab that was left open. The
 * damage is silent rather than loud: `useVaultJobs` swallows the failure and
 * keeps its last good page, so the activity feed freezes into something
 * indistinguishable from "nothing new happened" until a full reload.
 *
 * Re-minting on a timer is safe and does NOT breach the no-signing-without-a-
 * gesture rule: `requestDelegation` needs only `isAuthenticated` — no keys, no
 * ceremony, no popup (see this file's header). It is not a signature.
 *
 * The first mint is awaited and its failure propagates to the caller, which is
 * the call site's existing error path. Every LATER failure is handed to
 * `onError` and retried, because the current instance may still have headroom
 * and tearing the session down over a gateway blip would be worse than the
 * staleness.
 */
export async function mintRefreshingVaultReadClient(
  wallet: Pick<CereWallet, 'requestDelegation'>,
  a: Omit<CreateVaultReadClientArgs, 'token'>,
  handlers: RefreshingVaultReadClientHandlers,
): Promise<RefreshingVaultReadClient> {
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = (delayMs: number) => {
    if (disposed) return;
    timer = setTimeout(() => { void refresh(); }, delayMs);
  };

  const refresh = async () => {
    if (disposed) return;
    try {
      const next = await mintDelegation(wallet, a);
      // Re-check: `dispose()` can land while the mint is in flight, and a
      // logged-out session must not be handed a fresh client — that is the
      // bug this PR already fixed once for the jobs poll.
      if (disposed) return;
      handlers.onRefresh(next.sdk);
      schedule(readTokenRefreshDelayMs(next.expiresAt, Date.now()));
    } catch (e) {
      if (disposed) return;
      handlers.onError(e);
      schedule(VAULT_READ_REFRESH_RETRY_MS);
    }
  };

  const first = await mintDelegation(wallet, a);
  schedule(readTokenRefreshDelayMs(first.expiresAt, Date.now()));
  return {
    sdk: first.sdk,
    dispose: () => {
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}
